import { DropinSupervisorBase } from "SATPROF_LIBRARY/DropinSupervisor/dropin-supervisor-base";
import CurrierBase from "../SMS/CurrierBase";
import { LP5Channel } from "../SMS/LP5Channel";
import MessengerHub from "../SMS/MessengerHub";
import { LessonDataPage } from "./lesson-data-format-page";

const channelName = "LP5_PLAYER_CHANNEL";

export class Lp5PlayerAdapter {
  protected supervisor: DropinSupervisorBase;
  protected smsChannel: LP5Channel;
  protected inLP5: boolean = false;
  protected pageData: LessonDataPage;
  protected sms: MessengerHub | null;
  public inLp5(): boolean {
    return this.inLP5;
  }

  constructor(
    supervisor: DropinSupervisorBase,
    satprofMessengerServiceHub: MessengerHub = null // mostly for testing purposes
  ) {
    this.supervisor = supervisor;
    const sms = satprofMessengerServiceHub
      ? satprofMessengerServiceHub
      : this.supervisor.getSMS();

    // if we haven't set this flag, we're not in lp5
    // lp5 will also disable the default init call
    if (!window["LP5_PAGE"]) return;

    this.inLP5 = true;
    // establish an sms channel for communicating with lp5
    this.smsChannel = new LP5Channel();

    sms.addChannel(this.smsChannel);
    this.sms = sms;

    // now connect the player
    sms.subscribe(channelName, (message) => {
      // figure out what command we should be doing
      let command = null;
      if (typeof message == "string") {
        command = message.toUpperCase();
      } else if (message.command) {
        command = message.command.toUpperCase();
      }

      // trigger any command listeners that have been added
      // lets do what that lp5 player says
      switch (command) {
        // case LP5_RESPONSES.PAGE_DATA:
        //   // now we take the page data and use it to inform the dropin
        //   const payload = message.payload as LessonDataPage;
        //   this.pageData = payload;
        //   supervisor.setLessonPageData(payload);
        //   this.inLP5 = true;

        //   break;

        case LP5_RESPONSES.GOTO_PAGE:
          {
            // now we take the page data and use it to inform the dropin
            const payload = message.payload as { step?: number };
            if (payload?.step !== undefined) {
              supervisor.gotoStep(payload.step);
              return { success: true };
            }
          }
          break;

        case LP5_RESPONSES.PAGE_DATA:
          // now we take the page data and use it to inform the dropin
          const payload = message.payload as LessonDataPage;
          this.pageData = payload;
          supervisor.setLessonPageData(payload);

          break;
      }
    });

    const mode = getModeFromUrl(window);
    window.addEventListener("load", () => {
      // now announce ourselves
      this.sendCommandToLp5Player(
        LP5_COMMANDS.INIT,
        { version: DropinSupervisorBase.CurrentVersion },
        (response) => {
          const payload = JSON.parse(response).payload as {
            lessonDataPage: LessonDataPage;
            gotoStep?: number;
          };

          if (!payload.lessonDataPage)
            console.error("LP5 INIT FAILED, make sure you're using lp5 >1.3.1");
          this.pageData = payload.lessonDataPage;
          supervisor.setLessonPageData(payload.lessonDataPage);
          this.inLP5 = true;
          if (payload?.gotoStep) {
            supervisor.gotoStep(payload.gotoStep);
          }
          window["init"]();
        }
      );
    });
  }

  onFrameZero() {
    if (!this.inLp5()) return;
    const stepCount = this.supervisor.lastStep + 1;

    this.sendCommandToLp5Player(
      LP5_COMMANDS.FRAME_ZERO,
      {
        stepCount: stepCount,
      },
      (message) => {
        const payload = JSON.parse(message).payload as { gotoStep?: number };
        if (payload?.gotoStep !== undefined) {
          this.supervisor.gotoStep(payload.gotoStep);
        }
      }
    );
  }

  // ================= success/scoring ================= //
  setSuccess(successState: boolean) {
    if (!this.inLp5()) return false;
    this.sendCommandToLp5Player(LP5_COMMANDS.SET_SUCCESS, {
      success: successState,
    });
  }

  setScore(score: number) {
    if (!this.inLp5()) return false;
    this.sendCommandToLp5Player(LP5_COMMANDS.SET_SCORE, {
      score,
    });
  }

  // conditions under which the legacy "isWidget" should be used.
  isWidget() {
    if (!this.inLP5) return false;
    if (this.pageData?.mode && this.pageData?.mode == "exer") return false;

    return true;
  }

  // this bundles a command and a payload to send through SMS
  sendCommandToLp5Player(
    command: string,
    payload?: string | object,
    callback?: any
  ) {
    this.sendMessageToLp5Player({ command, payload }, callback);
  }

  // handles converting the message to a string
  sendMessageToLp5Player(messageObj: object, callback?: any) {
    if (!this.inLp5()) return;
    let rawMessage = JSON.stringify(messageObj);

    this.sms.send(channelName, rawMessage, callback);
    // this.smsChannel.sendMessage(channelName, rawMessage, undefined, callback);
  }
}

export const LP5_COMMANDS = {
  INIT: "LP5_INIT",
  FRAME_ZERO: "LP5_FRAME_ZERO",
  SET_SUCCESS: "LP5_SUCCESS",
  SET_SCORE: "LP5_SCORE",
} as const;

export const LP5_RESPONSES = {
  PAGE_DATA: "LP5_PAGE_DATA",
  GOTO_PAGE: "LP5_GOTO_PAGE",
} as const;

export enum LessonDataPageMode {
  DEFAULT = "step",
  STEP = "step", // STEP mode doesn't technically do anything
  EXERCISE = "exer",
  TEST = "test",
}

export const getModeFromUrl = (targetWindow: Window): any => {
  if (!targetWindow.location.hash) return null;
  // check the hash for get vars
  const queryString = new URLSearchParams(
    targetWindow.location.hash.substring(1)
  );

  let queryMode = queryString.get("mode");
  if (!queryMode) return null;

  // set it to lowercase
  queryMode = queryMode.toLowerCase();

  // limit it only to valid modes
  if (Object.values(LessonDataPageMode).includes(queryMode as any)) {
    return queryMode as LessonDataPageMode;
  }

  return null;
};
